import { KmzInvalidMessagesData, useInfiniteKmzInvalids } from "@/api/hptar";
import { Null } from "@/components/null";
import { DefaultButton } from "@/components/ui/button";
import { MISSING_ID_IN_ROUTE } from "@/utils/constant";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

export default function HPTARKMZInvalidsTable() {
  const { id } = useParams();
  if (!id) {
    throw new Error(MISSING_ID_IN_ROUTE);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [kmzInvalidsRef, setKmzInvalidsRef] = useState<
    KmzInvalidMessagesData[]
  >([]);
  const thClassName =
    "w-64 whitespace-nowrap bg-slate-100 p-4 font-medium first:sticky first:left-0 first:z-20 first:border-r first:shadow-sm";
  const tdClassName =
    "w-64 whitespace-normal bg-white p-4 first:sticky first:left-0 first:z-10 first:border-r first:shadow-sm";

  const {
    data: kmzInvalids,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteKmzInvalids({ rfsId: id });

  useEffect(() => {
    if (kmzInvalids?.pages) {
      // Flatten the paginated data into a single array of KmzInvalidMessagesData
      const allContent = kmzInvalids?.pages?.flatMap((page) => {
        // For each page, we need to extract the content and then flatten the messages
        return page.content.flatMap((item) => {
          // Each item has a key and an array of messages. We need to create a new array where each message is combined with the key.
          return item.message.map((msg) => ({
            key: item.key,
            field: msg.field,
            message: msg.message,
            value: msg.value,
            latitude: msg.latitude,
            longitude: msg.longitude,
          }));
        });
      });
      setKmzInvalidsRef(allContent);
    }
  }, [kmzInvalids]);

  if (kmzInvalidsRef.length === 0) {
    return <Null />;
  }

  return (
    <>
      <div className="flex items-center justify-between">
        <h5 className="font-medium leading-none tracking-tight">
          KMZ Validation Result
        </h5>
      </div>

      <div className="max-h-[70vh] overflow-auto">
        <table className="w-full table-auto">
          <thead className="sticky top-0 z-20 bg-slate-100 shadow-sm">
            <tr className="flex border-b text-left text-sm">
              <th className={thClassName}>Message</th>
              <th className={thClassName}>Folder</th>
              <th className={thClassName}>Name</th>
              <th className={thClassName}>Value</th>
              <th className={thClassName}>Latitude</th>
              <th className={thClassName}>Longitude</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {kmzInvalidsRef?.map((invalid) => (
              <tr className="flex text-sm">
                <td className={tdClassName}>{invalid.message}</td>
                <td className={tdClassName}>{invalid.key}</td>
                <td className={tdClassName}>{invalid.field}</td>
                <td className={tdClassName}>{invalid.value}</td>
                <td className={tdClassName}>{invalid.latitude ?? "-"}</td>
                <td className={tdClassName}>{invalid.longitude ?? "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-4 flex justify-center">
        {hasNextPage && (
          <DefaultButton
            mood="axiata-blue-outline"
            className="mx-auto"
            loading={isFetchingNextPage}
            onClick={() => {
              fetchNextPage();
            }}
          >
            {isFetchingNextPage ? "Loading..." : "Load More"}
          </DefaultButton>
        )}
      </div>
    </>
  );
}
